import React, { useState, useCallback, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useParams, Link } from "react-router-dom";
import {
  fetchSingleCultureCustoms,
  fetchRelatedCultureCustoms,
  toggleFavorite,
} from "../utilities/api";
import { Skeleton } from "../components/ui/skeleton";
import { mapCultureCustomsListData } from "../utilities";
import {
  LeftArrowIcon,
  DownloadIcon,
  PrintIcon,
  ExpandIcon,
  MiniMize,
  LinkIcon,
  FavoritesIcon,
} from "../components";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import { CCFamilyActivity, CCSacredMusic, CCrecipe } from "../templates";
import { contentTypeImages } from "./CultureCardTypes";
import { contentTypeSlugToName } from "../constants/contentTypeMap";

// Utility function to decode HTML entities
const decodeHtml = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

const HtmlRenderer = ({ htmlContent }) => {
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

// Static fallback data
const STATIC_FALLBACK = {
  title: "Culture & Customs Exploration",
  author: "John Doe",
  description:
    "Discover the rich tapestry of cultural traditions and customs from around the world.",
  image: `${process.env.PUBLIC_URL}/media/images/group-of-catechesis-title.jpeg`,
  openTag: "Culture & Customs",
  duration: "About 1 hr",
  categories: ["Heritage", "Traditions", "Global Culture"],
};

const SingleCultureCustoms = ({ passedId }) => {
  const printRef = useRef();
  const params = useParams();
  const slug = passedId || params.slug;
  const { copied, copyToClipboard } = useCopyToClipboard();

  console.log("SingleCultureCustoms - Params ID:", params);
  console.log("SingleCultureCustoms - Passed ID:", passedId);
  console.log("SingleCultureCustoms - Final ID:", slug);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [data, setData] = useState(STATIC_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to extract nationalities and seasons from included data
  const extractTaxonomies = (included, type) => {
    if (!Array.isArray(included)) {
      console.error("Included is not an array:", included);
      return [];
    }

    return included
      .filter((item) => item?.type === type && item.attributes?.name)
      .map((item) => item.attributes.name);
  };

  // Transform API data
  const transformApiData = useCallback((apiData) => {
    const result = apiData?.data;
    console.log(result, "result==>>>>>>>>>>", apiData);
    const item =
      apiData?.data?.attributes ||
      apiData?.attributes ||
      apiData ||
      STATIC_FALLBACK;
    const included = apiData?.included || [];
    console.log("API Included Data:", included); // Debugging log
    // Extract nationalities and seasons
    const nationalities = extractTaxonomies(included, "ps-nationalities");
    const seasons = extractTaxonomies(included, "ps-seasons");
    const authors = included
      .filter((inc) => inc.type === "ps-authors")
      .map((author) => ({
        name: author.attributes?.name || "Unknown Author",
        description:
          author.attributes.description &&
            author.attributes?.description.trim() !== ""
            ? decodeHtml(author.attributes.description)
            : "",
      }));
    const ps_content_type = result?.attributes?.ps_content_type;
    const ps_source_attribution = result?.attributes?.ps_source_attribution;
    const ps_source_url = result?.attributes?.ps_source_url;

    // Recipe specific fields
    const ps_ingredients = result?.attributes?.ps_ingredients;
    const ps_instructions = result?.attributes?.ps_instructions;
    const ps_prep_time = result?.attributes?.ps_prep_time;
    const ps_total_time = result?.attributes?.ps_total_time;
    const ps_portions = result?.attributes?.ps_portions;

    return {
      id: result.id,
      slug: item.slug,
      imageUrl:
        item?.image?.large ||
        contentTypeImages[contentTypeSlugToName[ps_content_type?.[0]]] ||
        STATIC_FALLBACK.image,
      title: decodeHtml(item.title),
      about: decodeHtml(item.content || STATIC_FALLBACK.description),
      description: decodeHtml(item.excerpt),
      openTag: decodeHtml(item.open_tag || STATIC_FALLBACK.openTag),
      duration: `About ${item.duration || STATIC_FALLBACK.duration}`,
      categories: [...nationalities, ...seasons], // Combine nationalities and seasons into categories
      favorite: Boolean(item.favorite || false),
      fullDescription: decodeHtml(
        item.full_description || item.description || STATIC_FALLBACK.description
      ),
      author: authors.length > 0 ? authors[0].name : "Unknown Author",
      authorDescription: authors.length > 0 ? authors[0].description : "",
      ps_source_url: ps_source_url.length > 0 ? ps_source_url[0] : "",
      ps_content_type: ps_content_type.length > 0 ? ps_content_type[0] : "",
      ps_source_attribution:
        ps_source_attribution.length > 0 ? ps_source_attribution[0] : "",

      // Recipe specific fields
      ps_ingredients: ps_ingredients.length > 0 ? ps_ingredients[0] : "",
      ps_instructions: ps_instructions.length > 0 ? ps_instructions[0] : "",
      ps_prep_time: ps_prep_time.length > 0 ? ps_prep_time[0] : "",
      ps_total_time: ps_total_time.length > 0 ? ps_total_time[0] : "",
      ps_portions: ps_portions.length > 0 ? ps_portions[0] : "",
    };
  }, []);

  // Toggle popup function
  const togglePopup = () => {
    if (isPopupVisible) {
      setIsClosing(true);
      setTimeout(() => {
        setIsPopupVisible(false);
        setIsClosing(false);
      }, 300);
    } else {
      setIsPopupVisible(true);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchSingleCultureCustoms(slug);
        console.log("Raw API Result:", result);

        if (result) {
          const processedData = transformApiData(result);
          console.log("Processed Data:", processedData);
          setData(processedData);
          // const related = await fetchRelatedCultureCustoms(result.data.id, signal);
          // console.log("Related Content:", related);
        } else {
          // Use static fallback if no data
          setError("No data found, using default content");
        }
      } catch (err) {
        console.error("Fetch Error:", err);
        // Set error message
        setError(`Failed to fetch data: ${err.message}`);
      } finally {
        setLoading(false);
        console.log("FInsihed loading");
      }
    };

    if (slug) {
      fetchData();
    }

    return () => controller.abort();
  }, [slug, transformApiData]);

  // Prevent body scroll when popup is visible
  useEffect(() => {
    if (isPopupVisible) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => document.body.classList.remove("overflow-hidden");
  }, [isPopupVisible]);

  // Modify loading and error states
  // if (loading)
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <p className="text-2xl text-gray-500">Loading...</p>
  //     </div>
  //   );

  const handleToggleFavorite = async (id) => {
    try {
      const currentFavorite = data.favorite;
      // Step 1: Optimistically update UI
      setData((prevData) =>
        prevData ? { ...prevData, favorite: !prevData.favorite } : null
      );
      // Step 2: Call API to update favorite status
      await toggleFavorite("culture-customs", data.id, false, currentFavorite);
    } catch (error) {
      console.error(" Failed to update favorite:", error);
      //  Step 3: Revert UI if API call fails
      setData((prevData) =>
        prevData ? { ...prevData, favorite: !prevData.favorite } : null
      );
    }
  };
  const handleBeforePrint = () => {
    if (window.trinity?.pauseAll) {
      try {
        window.trinity?.pauseAll();
      } catch (e) {
        console.warn("Failed to pause Trinity player before print", e);
      }
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: data?.title || "CultureCustomsPage",
    onBeforePrint: handleBeforePrint,
  });
  console.log("printRef.current:", printRef.current);

  const safeHandlePrint = useCallback(() => {
    if (typeof handlePrint !== "function") {
      console.error("handlePrint is not a function!");
      return;
    }

    try {
      const result = handlePrint?.(); // Call handlePrint safely

      //Check if it's a Promise before using `.then`
      if (result && typeof result.then === "function") {
        result.then(() => {
          console.log("Print completed successfully.");
        }).catch((err) => {
          console.error("Print failed (async):", err);
        });
      } else {
        console.log("Print was synchronous or returned undefined.");
      }
    } catch (err) {
      console.error("Print threw an error (sync):", err);
    }
  }, [handlePrint]);

  const handleNativePrint = () => {
    const content = printRef.current;
    if (!content) return alert("Nothing to print");

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return alert("Popup blocked. Please allow popups.");

    const styles = `
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
            <style>
            @page {  
            size: A4 portrait;
            margin: 0;
            }
                * { box-sizing: border-box; }
                img { max-width: 100%; max-height:100vh; object-fit:contain; height:auto; }
                @media print {
                body {
                    background: white !important;
                    color: black !important;
                    font-family: 'Red Hat Display', sans-serif !important;
                }
                }
                 .print-hidden,
    .print\\:hidden {
      display: none !important;
    }  
             @media print {
           .print\\:hidden {
              display: none !important;
           }
         }
            </style>
            `;


    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${data.title || 'Print Preview'}</title>
          ${styles}
          <style>
            * { box-sizing: border-box; }
            body { font-family: sans-serif; padding: 20px; margin: 0; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <div id="print-root">
            ${content.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Ensure print only after full content (like images) is loaded
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 500); // delay slightly to allow rendering
    };
  };

  console.log("data====>>>", data?.title);

  // const handleDownload = () => {
  //   if (!printRef.current) return;

  //   const pageContent = `
  //     <html>
  //       <head>
  //         <title>${data?.title || "Page"}</title>
  //         <style>
  //           body { font-family: Arial, sans-serif; padding: 20px; }
  //           img { max-width: 100%; height: auto; }
  //           /* Add any other styles you want */
  //         </style>
  //       </head>
  //       <body>
  //         ${printRef.current.innerHTML}
  //       </body>
  //     </html>
  //   `;

  //   const blob = new Blob([pageContent], { type: "text/html" });
  //   const url = URL.createObjectURL(blob);

  //   const link = document.createElement("a");
  //   link.href = url;
  //   link.download = `${data?.title || "page"}.html`;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   URL.revokeObjectURL(url);
  // };

  if (data?.ps_content_type && data?.ps_content_type == "ps-family-activity") {
    return (
      <CCFamilyActivity
        ref={printRef}
        data={data}
        loading={loading}
        handleToggleFavorite={handleToggleFavorite}
        handlePrint={handleNativePrint}

      />
    );
  } else if (
    data?.ps_content_type &&
    data?.ps_content_type == "ps-sacred-music"
  ) {
    return (
      <CCSacredMusic
        ref={printRef}

        data={data}
        loading={loading}
        handleToggleFavorite={handleToggleFavorite}
        handlePrint={handleNativePrint}

      />
    );
  } else if (data?.ps_content_type && data?.ps_content_type == "ps-recipe") {
    return (
      <CCrecipe
        ref={printRef}

        data={data}
        loading={loading}
        handleToggleFavorite={handleToggleFavorite}
        handlePrint={handleNativePrint}
      />
    );
  } else {
    return (
      <div className="h-full" ref={printRef}>
        <div >
          <div className="flex flex-col h-full px-8 lg:flex-row">
            {/* Left Sidebar */}
            <div className="w-full h-auto border-b border-gray-100 lg:w-1/3 lg:border-r lg:border-b-0">
              <div className="flex justify-between px-4 py-8 lg:px-6 print:hidden">
                <Link
                  className="flex items-center gap-3 group"
                  to="/culture-and-customs"
                >
                  {loading ? (
                    <Skeleton className="h-11 w-11 rounded-full" />
                  ) : (
                    <div className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 print:hidden">
                      <LeftArrowIcon className="text-gray-700 group-hover:text-gold-500" />
                    </div>
                  )}

                  <h5 className="print:hidden text-base text-gray-600 group-hover:text-gold-500">
                    Back to Culture & Customs
                  </h5>
                </Link>
              </div>
              <div className="px-4 pb-8">
                {loading ? (
                  <Skeleton className="h-8 w-3/4 mb-2 rounded" />
                ) : (
                  <h2 className="text-2xl font-bold text-gray-900 pb-0.5">
                    {data.title}
                  </h2>
                )}
                <div className="flex flex-row flex-wrap gap-2 mt-3">
                  {loading ? (
                    <>
                      <Skeleton className="w-full h-full rounded" />
                      {/* <Skeleton className="w-20 h-4 rounded-xl" />
                  <Skeleton className="w-24 h-4 rounded-xl" /> */}
                    </>
                  ) : (
                    data?.categories &&
                    data.categories.map((category, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-gold-50 text-gold-500 rounded-xl"
                      >
                        {category}
                      </span>
                    ))
                  )}
                  {/* CatList (Seasons and Nationalities) */}
                  {loading ? (
                    <Skeleton className="w-20 h-4 rounded-xl" />
                  ) : (
                    data?.catList &&
                    data.catList.map((cat, index) => (
                      <span
                        key={`cat-${index}`}
                        className="px-2 py-1 text-xs font-medium leading-4 bg-gold-50 text-gold-500 rounded-27"
                      >
                        {cat}
                      </span>
                    ))
                  )}
                </div>
                {/* <p className="text-gray-800">{data.description}</p> */}
              </div>
              <div className="px-4">
                <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">
                  Author:
                </h5>

                {loading ? (
                  <div className="flex items-center gap-2 mt-4">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <Skeleton className="h-4 w-32 rounded" />
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-2 mb-8">
                    <img src="/favicon.png" alt="Favicon" className="w-7 h-7" />
                    <p className="font-medium text-sm leading-4.5 text-gray-700">
                      {data?.author}
                    </p>
                  </div>
                )}

                {data?.authorDescription && (
                  <div>
                    <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">
                      About:
                    </h5>
                    <div>
                      {data.authorDescription ? (
                        <HtmlRenderer htmlContent={data?.authorDescription} />
                      ) : (
                        "No Description Available"
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Image Section */}
            <div className="w-full h-auto px-4 pt-8 pb-8 lg:w-2/3 lg:pt-0 xl:px-12">
              <div className="justify-end hidden gap-2 pt-8 pb-8 lg:flex print:hidden">
                {loading || !data ? (
                  <Skeleton className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group" />
                ) : (
                  <button
                    key={data.favorite} //  Forces re-render when favorite state changes
                    type="button"
                    onClick={(e) => handleToggleFavorite(data.id)}
                    className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                  >
                    <FavoritesIcon
                      filled={Boolean(data?.favorite)}
                      className={`w-7 h-7 transition-colors duration-200 ${data?.favorite
                        ? "text-red-500"
                        : "text-gray-500 hover:text-red-500"
                        }`}
                    />
                  </button>
                )}
                <button
                  type="button"
                  // onClick={handleDownload}
                  className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                >
                  <DownloadIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                </button>
                <button
                  type="button"
                  onClick={handleNativePrint}
                  className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                >
                  <PrintIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                </button>
                <a
                  href="#"
                  className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent navigation
                    copyToClipboard();
                  }}
                >
                  <LinkIcon
                    className={`text-gray-700 w-7 h-7 group-hover:text-gold-500 ${copied ? "text-green-500 group-hover:text-green-500" : ""}`}
                  />
                </a>
              </div>
              <div className="print-page-break relative w-full h-[80vh] object-cover rounded-3xl">
                {loading ? (
                  <Skeleton className="w-full h-full rounded-2xl mb-4" />
                ) : (
                  <img
                    className="print-img object-cover w-full h-full rounded-3xl"
                    src={data.imageUrl}
                    alt={data.title}
                  />
                )}
                {/* Expand Icon for Popup */}
                <div
                  className="absolute flex items-center justify-center transition rounded-full cursor-pointer bottom-4 right-4 h-11 w-11 bg-grayTransparent hover:scale-110"
                  onClick={togglePopup}
                >
                  <ExpandIcon className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Fullscreen Popup */}
        {isPopupVisible && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70"
            onMouseDown={togglePopup}
          >
            <div
              className={`relative max-w-[1040px] max-h-[616px] w-full h-full ${isClosing ? "animate-popupZoomIn" : "animate-popupZoomOut"
                }`}
            >
              <img
                className="object-cover w-full h-full rounded-3xl"
                src={data.imageUrl}
                alt={data.title}
              />
              {/* Minimize Button */}
              <button
                className="absolute flex items-center justify-center transition rounded-full bottom-4 right-4 h-11 w-11 bg-grayTransparent hover:scale-90"
                onClick={togglePopup}
              >
                <MiniMize className="text-white" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default SingleCultureCustoms;
